import {
  Button,
  Card,
  Form,
  FormControl,
  FormGroup,
  FormLabel,
  FormSelect,
  Modal,
} from 'react-bootstrap'
import { t } from '../locale'
import React, { useRef, useState, ChangeEvent, MouseEvent } from 'react'
import v from '../validators'
import { fetchClientById, updateStammdatenClient } from '../service/client'
import { showKAlert, updateNestedField } from '../helpers'
import { normalizeFiles, uploadStammdatenFile } from '../helpers/stammdaten.helper'

interface StammdatenShareModalProps {
  show: boolean
  onClose: () => void
  onAccept: () => void
}

export const StammdatenShareModal: React.FC<StammdatenShareModalProps> = ({
  show,
  onClose,
  onAccept,
}) => {
  const [isGenderValid, setIsGenderValid] = useState(true)
  const [isFirstNameValid, setIsFirstNameValid] = useState(true)
  const [isLastNameValid, setIsLastNameValid] = useState(true)
  const [isEmailValid, setIsEmailValid] = useState(true)

  const [gender, setGender] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  function handleEmail(e: any) {
    const value = e.target.value.trimStart()
    setEmail(value)
    setIsEmailValid(v.email(value))
  }
  function handleGender(e: any) {
    setGender(e.target.value)
    setIsGenderValid(v.gender(e.target.value))
  }
  function handleLastName(e: any) {
    const value = e.target.value.trimStart()
    setLastName(value)
    setIsLastNameValid(v.companyName(value))
  }
  function handleFirstName(e: any) {
    const value = e.target.value.trimStart()
    setFirstName(value)
    setIsFirstNameValid(v.name(value))
  }
  function handleNote(e: any) {
    setNote(e.target.value.trimStart())
  }

  const handleFileClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    fileInputRef.current?.click()
  }
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setSelectedFile(e.target.files[0])
    }
  }

  const share = async () => {
    const userAccess = JSON.parse(sessionStorage.getItem('userAccess') ?? '{}')
    const userEmail = localStorage.getItem('email') ?? 'null'

    const shareData = [
      {
        gender: gender,
        name: lastName,
        firstName: firstName,
        email: email,
        note: note,
      },
    ]
    const patch = updateNestedField({}, 'shared', shareData)
    const response = await updateStammdatenClient({
      clientId: localStorage.getItem('clientId'),
      stammdaten: patch,
      userName: userAccess.name ?? '',
      userEmail: userEmail ?? '',
    })

    if (response.ok) {
      if (selectedFile) {
        const clientId = localStorage.getItem('clientId') || ''
        const clientData = await fetchClientById(clientId)

        const entry = await uploadStammdatenFile({
          file: selectedFile,
          clientId,
          type: 'files',
          name: clientData.client.name,
          email,
        })

        const existingFiles = normalizeFiles(clientData, 'files')
        const updatedFiles = [...existingFiles, entry]

        const filesPatch = updateNestedField({}, 'files', updatedFiles)
        await updateStammdatenClient({
          clientId,
          stammdaten: filesPatch,
        })
      }

      showKAlert('success', 'sentToClient')

      onClose()
    }
  }

  const [validated, setValidated] = useState(false)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget

    if (!form.checkValidity()) {
      e.stopPropagation()
      setValidated(true) // da Bootstrap oboji invalid polja
      return
    }

    share()
  }

  return (
    <Modal show={show} onHide={onClose} centered dialogClassName='modal-xl modern-modal'>
      <Modal.Header closeButton>
        <Modal.Title className='modal-title'>{t('teilen')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='modal-body-content'>
          <Card>
            <Card.Body>
              <Form id='share-form' noValidate validated={validated} onSubmit={handleSubmit}>
                <FormGroup>
                  <FormLabel htmlFor='gender'>{t('gender')}</FormLabel>
                  <FormSelect
                    id='gender'
                    isInvalid={!isGenderValid}
                    value={gender}
                    onChange={handleGender}
                    className='w-25'
                  >
                    <option value=''>--</option>
                    <option value='M'>{t('male')}</option>
                    <option value='F'>{t('female')}</option>
                    <option value='other'>{t('other')}</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup className='mt-3'>
                  <FormLabel htmlFor='lastName'>{t('lastName')}</FormLabel>
                  <FormControl
                    id='lastName'
                    isInvalid={!isLastNameValid}
                    placeholder={t('lastName')}
                    value={lastName}
                    onChange={handleLastName}
                    autoComplete='off'
                  />
                </FormGroup>

                <FormGroup className='mt-3'>
                  <FormLabel htmlFor='firstName'>{t('firstName')}</FormLabel>
                  <FormControl
                    id='firstName'
                    isInvalid={!isFirstNameValid}
                    placeholder={t('firstName')}
                    value={firstName}
                    onChange={handleFirstName}
                    autoComplete='off'
                  />
                </FormGroup>

                <FormGroup className='mt-3'>
                  <FormLabel htmlFor='email'>{t('email')}</FormLabel>
                  <FormControl
                    id='email'
                    isInvalid={!isEmailValid}
                    placeholder={t('email')}
                    value={email}
                    onChange={handleEmail}
                    type='email'
                    required
                    autoComplete='off'
                  />
                </FormGroup>

                <FormGroup className='mt-3'>
                  <FormLabel htmlFor='note'>{t('informationFürDenMandanten')}</FormLabel>
                  <FormControl
                    as='textarea'
                    id='note'
                    className='no-capitalize'
                    placeholder={t('informationFürDenMandanten')}
                    value={note}
                    onChange={handleNote}
                    rows={3}
                  />
                </FormGroup>

                <FormGroup className='mt-3'>
                  <input
                    type='file'
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <Button variant='secondary' onClick={handleFileClick}>
                    {selectedFile ? selectedFile.name : t('chooseFiles')}
                  </Button>
                  {selectedFile && (
                    <small className='text-muted ms-2'>
                      {selectedFile.type} — {selectedFile.size} bytes
                    </small>
                  )}
                </FormGroup>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='success' type='submit' form='share-form' className='accept-button'>
          {t('teilen')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
